import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Auth.css';

function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    agreementPersonalData: false,
    agreementPrivacy: false,
    agreementTerms: false,
    agreementMarketing: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const formatPhoneNumber = (value) => {
    // Удаляем все нецифровые символы
    const cleaned = value.replace(/\D/g, '');
    
    // Применяем маску +7 (XXX) XXX-XX-XX
    let formatted = '+7';
    if (cleaned.length > 1) {
      formatted += ' (' + cleaned.substring(1, 4);
    }
    if (cleaned.length >= 5) {
      formatted += ') ' + cleaned.substring(4, 7);
    }
    if (cleaned.length >= 8) {
      formatted += '-' + cleaned.substring(7, 9);
    }
    if (cleaned.length >= 10) {
      formatted += '-' + cleaned.substring(9, 11);
    }
    
    return formatted;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length <= 11) {
        setFormData(prev => ({
          ...prev,
          phone: formatPhoneNumber(cleaned)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName || !formData.firstName.trim()) {
      newErrors.firstName = 'Укажите имя';
    }
    if (!formData.lastName || !formData.lastName.trim()) {
      newErrors.lastName = 'Укажите фамилию';
    }
    const cleanedPhone = formData.phone.replace(/\D/g, '');
    if (!formData.phone || cleanedPhone.length < 11) {
      newErrors.phone = 'Укажите корректный номер телефона';
    }
    if (!formData.email || !formData.email.includes('@')) {
      newErrors.email = 'Укажите корректный email';
    }
    if (!formData.agreementPersonalData) {
      newErrors.agreementPersonalData = 'Необходимо согласие на обработку персональных данных';
    }
    if (!formData.agreementPrivacy) {
      newErrors.agreementPrivacy = 'Необходимо ознакомиться с Политикой конфиденциальности';
    }
    if (!formData.agreementTerms) {
      newErrors.agreementTerms = 'Необходимо принять условия Пользовательского соглашения';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      // Очищаем номер телефона от форматирования перед отправкой
      const cleanedPhone = formData.phone.replace(/\D/g, '');
      
      const response = await fetch('/api/v1/auth/telegram/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: cleanedPhone,
          email: formData.email,
          marketing_consent: formData.agreementMarketing
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Редирект на страницу ввода кода с передачей номера телефона
        navigate(`/verify?phone=${encodeURIComponent(cleanedPhone)}`, { replace: true });
      } else {
        setErrors({ general: data.detail || 'Ошибка регистрации' });
      }
    } catch (err) {
      console.error('Signup error:', err);
      setErrors({ general: 'Ошибка соединения с сервером' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-side">
          <div className="auth-side-content">
            <a href="https://markapp.pro" className="auth-logo">
              <img src="/markapp-logo-white.png?v=20251111" alt="MarkApp" className="auth-logo-image" />
            </a>

            <h1 className="auth-side-title">Начните работать с маркированной рекламой</h1>
            <p className="auth-side-description">
              Присоединяйтесь к сотням маркетологов, которые автоматизировали маркировку и отчетность
            </p>

            <div className="auth-benefits">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="rgba(255,255,255,0.15)"/>
                    <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="benefit-text">
                  <div className="benefit-title">5 дней бесплатно</div>
                  <div className="benefit-desc">Без привязки карты</div>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13 7L15.5 3L15 8L18 4.5L16.5 9L20 7L17 11L21 10L17.5 13.5L22 14L17 16L20 18L15 17L17 20L13 17.5L14 22L12 17.5L10 22L11 17.5L7 20L9 17L5 18L8 16L2 14L6.5 13.5L3 11L7 10L4 7L7.5 9L6 4.5L9 8L8.5 3L11 7L10 2L12 2Z" stroke="white" strokeWidth="1.5" fill="rgba(255,255,255,0.15)"/>
                    <path d="M12 17V22" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M9 19L12 22L15 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="benefit-text">
                  <div className="benefit-title">2 минуты до запуска</div>
                  <div className="benefit-desc">Быстрая регистрация</div>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="2" width="12" height="20" rx="2" stroke="white" strokeWidth="2" fill="rgba(255,255,255,0.15)"/>
                    <path d="M10 5H14" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="18" r="1.5" fill="white"/>
                  </svg>
                </div>
                <div className="benefit-text">
                  <div className="benefit-title">iOS приложение</div>
                  <div className="benefit-desc">Бонус для подписчиков</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-form-panel">
          <div className="auth-form-container">
            <div className="auth-header">
              <h2 className="auth-title">Создать аккаунт</h2>
            </div>

            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first-name" className="form-label">
                    Имя <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="first-name"
                    name="firstName"
                    className="form-input"
                    placeholder="Иван"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    autoComplete="given-name"
                  />
                  {errors.firstName && <div className="form-error show">{errors.firstName}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="last-name" className="form-label">
                    Фамилия <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="last-name"
                    name="lastName"
                    className="form-input"
                    placeholder="Иванов"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    autoComplete="family-name"
                  />
                  {errors.lastName && <div className="form-error show">{errors.lastName}</div>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Номер телефона <span className="required">*</span>
                </label>
                <div className="input-with-icon">
                  <span className="input-icon">📱</span>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input"
                    placeholder="+7 (900) 123-45-67"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    autoComplete="tel"
                  />
                </div>
                {errors.phone && <div className="form-error show">{errors.phone}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email <span className="required">*</span>
                </label>
                <div className="input-with-icon">
                  <span className="input-icon">✉️</span>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    placeholder="ivan@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                </div>
                {errors.email && <div className="form-error show">{errors.email}</div>}
              </div>

              <div className="form-agreements">
                <div className="form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      id="agreement-personal-data"
                      name="agreementPersonalData"
                      checked={formData.agreementPersonalData}
                      onChange={handleChange}
                      required
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">
                      Я даю согласие на{' '}
                      <a href="https://markapp.pro/personal-data-agreement.html" target="_blank" rel="noopener noreferrer">
                        обработку персональных данных
                      </a>
                      <span className="required">*</span>
                    </span>
                  </label>
                  {errors.agreementPersonalData && <div className="form-error show">{errors.agreementPersonalData}</div>}
                </div>

                <div className="form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      id="agreement-privacy"
                      name="agreementPrivacy"
                      checked={formData.agreementPrivacy}
                      onChange={handleChange}
                      required
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">
                      Я ознакомлен с{' '}
                      <a href="https://markapp.pro/privacy-policy.html" target="_blank" rel="noopener noreferrer">
                        Политикой конфиденциальности
                      </a>
                      <span className="required">*</span>
                    </span>
                  </label>
                  {errors.agreementPrivacy && <div className="form-error show">{errors.agreementPrivacy}</div>}
                </div>

                <div className="form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      id="agreement-terms"
                      name="agreementTerms"
                      checked={formData.agreementTerms}
                      onChange={handleChange}
                      required
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">
                      Я принимаю условия <a href="https://markapp.pro/terms-of-service.html" target="_blank" rel="noopener noreferrer">Пользовательского соглашения</a> и <a href="https://markapp.pro/oferta.html" target="_blank" rel="noopener noreferrer">Договора оферты</a>
                      <span className="required">*</span>
                    </span>
                  </label>
                  {errors.agreementTerms && <div className="form-error show">{errors.agreementTerms}</div>}
                </div>

                <div className="form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      id="agreement-marketing"
                      name="agreementMarketing"
                      checked={formData.agreementMarketing}
                      onChange={handleChange}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">
                      Я согласен получать новости и специальные предложения
                    </span>
                  </label>
                </div>
              </div>

              {errors.general && <div className="form-error show">{errors.general}</div>}

              <button type="submit" className={`btn-primary btn-block btn-auth ${loading ? 'loading' : ''}`} disabled={loading}>
                <span className="btn-text">Зарегистрироваться</span>
                {loading && (
                  <span className="btn-loader">
                    <svg className="spinner" width="20" height="20" viewBox="0 0 20 20">
                      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="50" strokeDashoffset="25"/>
                    </svg>
                  </span>
                )}
              </button>

              <div className="auth-switch">
                Уже есть аккаунт? <Link to="/login">Войти</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
